import { useEffect, useState } from "react";
import { CreditCard, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// This whole panel is only ever reachable via /admin/organizations, which the
// router already gates with <ProtectedRoute requireSuperadmin> (see App.js) —
// the backend independently enforces require_superadmin() on both endpoints
// below regardless, so there is no reliance on this frontend gate alone.

const PROVIDER_OPTIONS = [
  { value: "", label: "Platform standaard (Pay.nl van StudioVoorJou)" },
  { value: "paynl", label: "Eigen Pay.nl-account" },
  { value: "mollie", label: "Mollie" },
];

export default function AdminOrgPaymentProvider({ org }) {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [provider, setProvider] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [secret, setSecret] = useState("");
  const [apiKey, setApiKey] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`/admin/organizations/${org.id}/payment-provider`);
      setStatus(data);
      setProvider(data.payment_provider || "");
      setServiceId("");
      setSecret("");
      setApiKey("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id]);

  const save = async () => {
    setSaving(true);
    try {
      let payload;
      if (!provider) {
        payload = { payment_provider: null, payment_credentials: null };
      } else if (provider === "mollie") {
        payload = { payment_provider: "mollie", payment_credentials: { api_key: apiKey } };
      } else {
        payload = { payment_provider: "paynl", payment_credentials: { service_id: serviceId, secret } };
      }
      await authAxios.patch(`/admin/organizations/${org.id}/payment-provider`, payload);
      toast.success("Betaalprovider opgeslagen");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-svj-border bg-svj-bg/40 p-5" data-testid={`org-payment-provider-${org?.id}`}>
      <div className="flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-svj-cyan" />
        <div className="text-sm font-semibold text-white uppercase tracking-wider">Betaalprovider</div>
        <span className="text-[10px] text-slate-500">(alleen superadmin)</span>
      </div>

      {loading ? (
        <div className="mt-3 text-sm text-slate-400 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Laden…
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <label className="block max-w-sm">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Provider</span>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              data-testid={`org-payment-provider-select-${org.id}`}
              className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            >
              {PROVIDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          {!provider && (
            <div className="text-xs text-slate-400">
              Platform-standaard is {status?.platform_default_configured ? "geconfigureerd" : "niet geconfigureerd"}.
            </div>
          )}

          {provider === "paynl" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Service ID</span>
                <input
                  type="text"
                  value={serviceId}
                  onChange={(e) => setServiceId(e.target.value)}
                  placeholder={status?.credentials_present?.service_id ? "•••• (geconfigureerd)" : "SL-xxxx"}
                  data-testid={`org-payment-paynl-service-id-${org.id}`}
                  className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Secret</span>
                <input
                  type="password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder={status?.credentials_configured ? "•••• (geconfigureerd)" : ""}
                  data-testid={`org-payment-paynl-secret-${org.id}`}
                  className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
                />
              </label>
              <div className="sm:col-span-2 text-[10px] text-slate-500">
                Vul beide velden opnieuw in om op te slaan — API-secrets worden nooit teruggestuurd naar de browser, dus ze kunnen niet automatisch behouden blijven.
              </div>
            </div>
          )}

          {provider === "mollie" && (
            <label className="block max-w-sm">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Mollie API key</span>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={status?.credentials_configured ? "•••• (geconfigureerd)" : "test_... of live_..."}
                data-testid={`org-payment-mollie-key-${org.id}`}
                className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
              />
              <div className="mt-1 text-[10px] text-slate-500">
                Vul de key opnieuw in om op te slaan — deze wordt nooit teruggestuurd naar de browser.
              </div>
            </label>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid={`org-payment-provider-save-${org.id}`}
              className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-2 inline-flex items-center gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Opslaan
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
